import { useContext, useRef, useState } from "react";
import { IMessage } from "react-native-gifted-chat";
import { AppStoreContext } from "../provider/AppStoreProvider";

export const useUnsyncChatIndexedDB = () => {

    const appContext = useContext(AppStoreContext);

    const _openChatDb = async (): Promise<IDBDatabase> => {
        if (appContext.unsyncChatDatabase?.value) {
            return appContext.unsyncChatDatabase!.value;
        }

        const request = window.indexedDB.open("unsync_therapist_chat", 1);
        return await new Promise((resolve: any, reject: any) => {
            request.onerror = (event) => {
                reject(event);
            };
            request.onsuccess = (event) => {
                appContext.unsyncChatDatabase?.set(request.result);
                resolve(request.result);
            };
            request.onupgradeneeded = (event) => {
                const store = request.result.createObjectStore("messages", { keyPath: "_id", autoIncrement: false });
                return new Promise((resolve: any, reject: any) => {
                    store.transaction.oncomplete = (event) => {
                        appContext.unsyncChatDatabase?.set(request.result);
                        resolve(request.result);
                    }
                });
            };
        });
    };

    const loadUnsyncMessages = async (): Promise<IMessage[]> => {
        const database = await _openChatDb();
        const messages: IMessage[] = [];
        return await new Promise((resolve, reject) => {
            const request = database
                .transaction("messages", "readonly")
                .objectStore("messages")
                .openCursor(null, "prev");

            request.onerror = (event) => {
                reject(event);
            };

            request.onsuccess = (event) => {
                const cursor = request.result;
                if (cursor) {
                    messages.push(cursor.value);
                    cursor.continue();
                } else {
                    resolve(messages.sort((a, b) => {
                        const dateA = new Date(a.createdAt);
                        const dateB = new Date(b.createdAt);
                        return dateA.getTime() - dateB.getTime();
                    }));
                }
            };
        });
    };

    const storeUnsyncMessage = async (message: IMessage): Promise<void> => {
        const database = await _openChatDb();
        return await new Promise((resolve, reject) => {
            const request = database
                .transaction("messages", "readwrite")
                .objectStore("messages")
                .add(message);

            request.onerror = (event) => {
                reject(event);
            };

            request.onsuccess = (event) => {
                resolve();
            };
        });
    };

    const clearUnsyncMessages = async (): Promise<void> => {
        const database = await _openChatDb();
        return await new Promise((resolve, reject) => {
            const request = database
                .transaction("messages", "readwrite")
                .objectStore("messages")
                .clear();

            request.onerror = (event) => {
                reject(event);
            };

            request.onsuccess = (event) => {
                resolve();
            };
        });
    };

    const removeUnsyncMessage = async (message: IMessage): Promise<void> => {
        const database = await _openChatDb();
        return await new Promise((resolve, reject) => {
            const request = database
                .transaction("messages", "readwrite")
                .objectStore("messages")
                .delete(message._id);
            request.onerror = (event) => {
                reject(event);
            };
            request.onsuccess = (event) => {
                resolve();
            };
        });
    };

    return {
        loadUnsyncMessages,
        storeUnsyncMessage,
        clearUnsyncMessages,
        removeUnsyncMessage
    };
};
